import { useState, useCallback } from 'react';
import {
  Plane,
  MessageSquare,
  Shield,
  Info,
  Users,
  Phone,
  DollarSign,
  Copy,
  Check,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { TravelDataTab } from './tabs/TravelDataTab';
import { RemarksTab } from './tabs/RemarksTab';
import { SSRsTab } from './tabs/SSRsTab';
import { OtherServiceInfoTab } from './tabs/OtherServiceInfoTab';
import { TravelersTab } from './tabs/TravelersTab';
import { ContactsTab } from './tabs/ContactsTab';
import { PricingTab } from './tabs/PricingTab';
import type { BookingData, Dictionaries } from './utils/types';

interface PolentaViewerProps {
  bookingData: BookingData;
  dictionaries: Dictionaries;
  isLoading?: boolean;
}

type TabId = 'travel' | 'remarks' | 'ssrs' | 'osi' | 'travelers' | 'contacts' | 'pricing';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  count?: number;
}

// Copy button component with feedback
function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded transition-colors',
        copied
          ? 'bg-green-100 text-green-700'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      )}
      title={`Copy ${label}`}
    >
      {copied ? (
        <Check className="h-3 w-3" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
      <span>{label}</span>
    </button>
  );
}

export function PolentaViewer({ bookingData, dictionaries, isLoading }: PolentaViewerProps) {
  const [activeTab, setActiveTab] = useState<TabId>('travel');

  // Early return for loading state
  if (isLoading) {
    return <PolentaViewerSkeleton />;
  }

  // Defensive check: if bookingData is undefined or has errors array, show error state
  if (!bookingData || (bookingData as any).errors) {
    return (
      <div className="flex flex-col h-full bg-slate-50 items-center justify-center">
        <div className="text-center p-8">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
            <svg
              className="h-8 w-8 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Buchungsdaten nicht verfügbar
          </h3>
          <p className="text-sm text-slate-500">
            Die Buchungsdaten konnten nicht geladen werden.
          </p>
        </div>
      </div>
    );
  }

  // Get passenger names for copy
  const passengerNames = bookingData.travelers
    ?.map(t => {
      const name = t.names?.[0];
      return name ? `${name.lastName}/${name.firstName}` : null;
    })
    .filter(Boolean)
    .join(', ') || '';

  // Get ticket numbers for copy
  const ticketNumbers = bookingData.travelDocuments
    ?.map(t => t.id)
    .filter(Boolean)
    .join(', ') || '';

  // Get emails for copy
  const emails = bookingData.contacts
    ?.filter(c => c.contactType === 'Email' && c.address)
    .map(c => c.address)
    .join(', ') || '';

  // Get phones for copy
  const phones = bookingData.contacts
    ?.filter(c => c.contactType === 'Phone' && c.number)
    .map(c => c.number)
    .join(', ') || '';

  const tabs: Tab[] = [
    {
      id: 'travel',
      label: 'Travel Data',
      icon: <Plane className="h-4 w-4" />,
      count: bookingData.air?.bounds?.reduce((acc, b) => acc + b.flights.length, 0) || 0,
    },
    {
      id: 'remarks',
      label: 'Remarks',
      icon: <MessageSquare className="h-4 w-4" />,
      count: bookingData.remarks?.length || 0,
    },
    {
      id: 'ssrs',
      label: 'SSRs',
      icon: <Shield className="h-4 w-4" />,
      count: bookingData.specialServiceRequests?.length || 0,
    },
    {
      id: 'osi',
      label: 'Other Service Info',
      icon: <Info className="h-4 w-4" />,
      count: bookingData.otherServiceInformations?.length || 0,
    },
    {
      id: 'travelers',
      label: 'Travelers',
      icon: <Users className="h-4 w-4" />,
      count: bookingData.travelers?.length || 0,
    },
    {
      id: 'contacts',
      label: 'Contacts',
      icon: <Phone className="h-4 w-4" />,
      count: bookingData.contacts?.length || 0,
    },
    {
      id: 'pricing',
      label: 'Pricing',
      icon: <DollarSign className="h-4 w-4" />,
    },
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* Quick copy actions header */}
      <div className="shrink-0 bg-white border-b border-slate-100">
        <div className="px-3 md:px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="font-mono font-semibold text-slate-700">{bookingData.id}</span>
            <span className="text-slate-300">|</span>
            <span>{bookingData.travelers?.length || 0} traveler{(bookingData.travelers?.length || 0) !== 1 ? 's' : ''}</span>
          </div>
          {/* Hide copy buttons on mobile */}
          <div className="hidden md:flex items-center gap-2">
            <CopyButton text={bookingData.id} label="PNR" />
            {passengerNames && <CopyButton text={passengerNames} label="Names" />}
            {ticketNumbers && <CopyButton text={ticketNumbers} label="Tickets" />}
            {emails && <CopyButton text={emails} label="Emails" />}
            {phones && <CopyButton text={phones} label="Phones" />}
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="shrink-0 bg-white border-b border-slate-200">
        <div className="px-2 md:px-6">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap min-h-[44px]',
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                )}
              >
                {tab.icon}
                {/* Hide labels on mobile, show on md and up */}
                <span className="hidden md:inline">{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span
                    className={cn(
                      'inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium',
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-slate-100 text-slate-600'
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto p-3 md:p-6">
        <div className="max-w-7xl mx-auto">
          {activeTab === 'travel' && (
            <TravelDataTab data={bookingData} dictionaries={dictionaries} />
          )}
          {activeTab === 'remarks' && (
            <RemarksTab remarks={bookingData.remarks || []} />
          )}
          {activeTab === 'ssrs' && (
            <SSRsTab
              ssrs={bookingData.specialServiceRequests || []}
              dictionaries={dictionaries}
            />
          )}
          {activeTab === 'osi' && (
            <OtherServiceInfoTab
              otherServiceInfos={bookingData.otherServiceInformations || []}
            />
          )}
          {activeTab === 'travelers' && (
            <TravelersTab travelers={bookingData.travelers || []} />
          )}
          {activeTab === 'contacts' && (
            <ContactsTab
              contacts={bookingData.contacts || []}
              travelers={bookingData.travelers || []}
            />
          )}
          {activeTab === 'pricing' && (
            <PricingTab
              prices={bookingData.air?.prices}
              travelers={bookingData.travelers || []}
              dictionaries={dictionaries}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PolentaViewerSkeleton() {
  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* Tab navigation skeleton */}
      <div className="shrink-0 bg-white border-b border-slate-200">
        <div className="px-6">
          <nav className="flex gap-1 -mb-px">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-3"
              >
                <div className="w-4 h-4 bg-slate-200 rounded animate-pulse" />
                <div className="w-16 h-4 bg-slate-200 rounded animate-pulse" />
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Content skeleton */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left column */}
            <div className="space-y-4">
              <div className="h-6 w-40 bg-slate-200 rounded animate-pulse" />
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-slate-200 rounded-full animate-pulse" />
                    <div className="space-y-2">
                      <div className="w-24 h-5 bg-slate-200 rounded animate-pulse" />
                      <div className="w-16 h-3 bg-slate-200 rounded animate-pulse" />
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="w-20 h-4 bg-slate-200 rounded animate-pulse" />
                    <div className="w-10 h-10 bg-slate-200 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>

            {/* Right column */}
            <div className="space-y-4">
              <div className="h-6 w-32 bg-slate-200 rounded animate-pulse" />
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-slate-200 rounded-lg animate-pulse" />
                    <div className="space-y-2 flex-1">
                      <div className="w-32 h-5 bg-slate-200 rounded animate-pulse" />
                      <div className="w-24 h-3 bg-slate-200 rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PolentaViewer;
